import { describeError } from '@cf-shared/error-describe';
import { Food, isOwnedUserFood } from '@cf-shared/data-model/food';
import { isIngredientReal } from '@cf-shared/data-model/ingredient';
import { isLegacyMealId, isRecipe, Recipe } from '@cf-shared/data-model/recipe';

/** What the walk needs from the app, passed in so the walk runs under the node test runner. */
export interface RecipeIngredientPublishDeps {
  /** the signed-in user; only foods stamped with this id are published */
  uid: string | undefined;
  /** which of these food ids the shared food database already holds */
  getPublishedIdsAsync(ids: string[]): Promise<Set<string>>;
  /** read one food. Undefined means it does not exist; a read that could not complete throws */
  readFoodAsync(id: string): Promise<Food | undefined>;
  /** publish one food. A refusal comes back as `{ error }`, not as a throw */
  publishAsync(food: Food): Promise<{ error?: any }>;
  /** whether a thrown publish means the device could not reach the server */
  isNetworkError(err: unknown): boolean;
  reportProblem(name: string, context: object): void;
}

/**
 * Put every ingredient of a shared recipe that the shared food database lacks into it,
 * looking inside sub-recipes too, and answer how many could not be published (#3255).
 *
 * People who open or import a shared recipe keep its ingredient ids, so each ingredient
 * that was never published shows to them as a blank row. It used to happen because
 * saving a shared recipe published only the recipe, and the first share published only
 * its direct ingredients.
 *
 * - Only foods this user owns are published (`isOwnedUserFood`). Anything else missing
 *   is counted and reported, never published: see `isOwnedUserFood` for why a food with
 *   no author stamp counts as not owned.
 * - A sub-recipe that is already published is still looked inside when it is this user's
 *   own or one they shared, because the first share used to publish sub-recipes without
 *   their ingredients.
 * - Every recipe is visited once, so a recipe that contains itself cannot loop.
 * - It stops at the first publish that cannot reach the server, by rethrowing, rather
 *   than sitting through the retry schedule once per remaining ingredient. A read that
 *   could not complete is not caught either. The caller decides: the edit page tells the
 *   user, the start-up repair stops and tries again next start.
 *
 * The published sub-recipe is not marked as shared and keeps its author stamp, exactly as
 * the first share has always left it. See design-docs/sync-integrity.md, "Publishing a
 * shared recipe's ingredients".
 */
export async function publishMissingRecipeIngredientsAsync(recipe: Recipe, deps: RecipeIngredientPublishDeps): Promise<number> {
  let failed = 0;
  const visited = new Set<string>([recipe._id]);
  let recipesToWalk: Recipe[] = [recipe];

  // one level of sub-recipes at a time, so each level costs one shared-database lookup
  while (recipesToWalk.length > 0) {
    const ingredientIds = collectUnvisitedIngredientIds(recipesToWalk, visited);
    recipesToWalk = [];
    if (ingredientIds.length === 0) break;

    const publishedIds = await deps.getPublishedIdsAsync(ingredientIds);
    for (const foodId of ingredientIds) {
      const outcome = await handleIngredientAsync(foodId, publishedIds.has(foodId), recipe._id, deps);
      if (outcome.failed) failed++;
      if (outcome.walkInto) recipesToWalk.push(outcome.walkInto);
    }
  }
  return failed;
}

/** The real ingredient ids of these recipes that have not been looked at yet, marked as looked at. */
function collectUnvisitedIngredientIds(recipes: Recipe[], visited: Set<string>): string[] {
  const ids: string[] = [];
  for (const walked of recipes) {
    // a damaged or old-shaped recipe may have no ingredient list
    const ingredients = Array.isArray(walked.ingredients) ? walked.ingredients : [];
    for (const ingredient of ingredients) {
      const foodId = isIngredientReal(ingredient) ? ingredient.foodId : undefined;
      if (typeof foodId !== 'string' || visited.has(foodId) || isLegacyMealId(foodId)) continue;
      visited.add(foodId);
      ids.push(foodId);
    }
  }
  return ids;
}

/**
 * Publish one ingredient if it needs it. Answers whether it failed, and which recipe (if
 * any) must be looked inside next.
 */
async function handleIngredientAsync(
  foodId: string, isPublished: boolean, rootRecipeId: string, deps: RecipeIngredientPublishDeps,
): Promise<{ failed: boolean, walkInto?: Recipe }> {
  const context = { recipeId: rootRecipeId, foodId };

  // if it is already published, only a user recipe can still hide missing ingredients
  if (isPublished) {
    if (!isUserRecipeId(foodId)) return { failed: false };
    const published = await deps.readFoodAsync(foodId);
    return { failed: false, walkInto: isOwnOrSharedRecipe(published, deps.uid) ? published as Recipe : undefined };
  }

  const food = await deps.readFoodAsync(foodId);
  if (!food) {
    deps.reportProblem('recipe-ingredient-publish: ingredient not found', context);
    return { failed: true };
  }
  if (!isOwnedUserFood(food, deps.uid)) {
    deps.reportProblem('recipe-ingredient-publish: ingredient not owned', context);
    return { failed: true };
  }

  let result: { error?: any };
  try {
    result = await deps.publishAsync(food);
  } catch (e) {
    // no connection: every later publish would sit through the same retries, so stop here
    if (deps.isNetworkError(e)) throw e;
    deps.reportProblem('recipe-ingredient-publish: publish failed', { ...context, detail: describeError(e) });
    return { failed: true };
  }
  if (result?.error) {
    deps.reportProblem('recipe-ingredient-publish: publish refused', { ...context, detail: describeError(result.error) });
    return { failed: true };
  }
  return { failed: false, walkInto: isRecipe(food) ? food : undefined };
}

// a recipe some user created, as opposed to one of our official recipes
function isUserRecipeId(foodId: string): boolean {
  return foodId.startsWith('food/+UDR-');
}

/**
 * Whether a published recipe is one to look inside. A recipe this user shared has lost its
 * author stamp (the start-up sweep clears the stamp from every published food except an
 * unshared recipe), so being shared from this device counts too. Only the user's own device
 * copy carries `local.shared`; the shared database's copy carries no `local` at all.
 */
function isOwnOrSharedRecipe(food: Food | undefined, uid: string | undefined): boolean {
  return !!food && isRecipe(food) && (isOwnedUserFood(food, uid) || food.local?.shared === true);
}

/** What the start-up repair needs from the app. */
export interface SharedRecipeRepairDeps {
  publishMissingIngredientsAsync(recipe: Recipe): Promise<number>;
  /** whether an error means "could not reach the database or the server right now" */
  isTransientFailure(err: unknown): boolean;
  reportProblem(name: string, context: object): void;
}

/**
 * Repair recipes shared before #3255, one at a time, so a user with many shared recipes
 * does not fire a burst of publishes at start-up. Stops without a report at the first
 * failure that only means "offline" or "the database is recovering": nothing is lost, and
 * the next app start tries again. Any other failure is reported, and stops the pass too.
 */
export async function repairSharedRecipesAsync(sharedRecipes: Recipe[], deps: SharedRecipeRepairDeps): Promise<void> {
  for (const recipe of sharedRecipes) {
    try {
      await deps.publishMissingIngredientsAsync(recipe);
    } catch (err) {
      if (!deps.isTransientFailure(err)) {
        deps.reportProblem('recipe-ingredient-publish: start-up repair failed', { recipeId: recipe._id, detail: describeError(err) });
      }
      return;
    }
  }
}

/** Where `reportOncePerDevice` keeps what it already reported. */
export const REPORTED_INGREDIENT_PROBLEMS_KEY = 'recipeIngredientPublish.reported';
/** How many reported problems it remembers, so the stored list cannot grow without limit. */
const MAX_REMEMBERED_PROBLEMS = 200;

/**
 * Wrap a problem reporter so each (problem, recipe, food) is reported once per device.
 *
 * The start-up repair walks every shared recipe on every start. An ingredient the user
 * deleted, or one they do not own, can never be published, so without this the same
 * problem would be reported on every start, for ever. The remembered list lives in
 * localStorage, is wiped at sign-out like every user-scoped key, and is trimmed to the
 * newest entries. If storage cannot be read or written, it reports every time rather than
 * risk losing a report.
 */
export function reportOncePerDevice(storage: Pick<Storage, 'getItem' | 'setItem'>, report: (name: string, context: object) => void) {
  return (name: string, context: { recipeId?: string, foodId?: string }) => {
    const key = `${name}|${context.recipeId}|${context.foodId}`;
    let remembered: string[] = [];
    try {
      const parsed = JSON.parse(storage.getItem(REPORTED_INGREDIENT_PROBLEMS_KEY) ?? '[]');
      // a damaged stored value is read as "nothing remembered"
      remembered = Array.isArray(parsed) ? parsed.filter(k => typeof k === 'string') : [];
    } catch {
      // unreadable storage means nothing remembered, so the problem below is reported anyway
      remembered = [];
    }
    if (remembered.includes(key)) return;
    report(name, context);
    try {
      storage.setItem(REPORTED_INGREDIENT_PROBLEMS_KEY, JSON.stringify([...remembered, key].slice(-MAX_REMEMBERED_PROBLEMS)));
    } catch {
      // storage full or blocked: the report went out, it may go out again next start
    }
  };
}
